import { Tag } from 'react-tag-autocomplete';

export const MAX_RECIPIENTS = 4000;
export const TAG_DISPLAY_LIMIT = 10;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/** Split pasted or typed lists on commas, semicolons, tabs, or newlines. */
export function parseRecipientInput(input: string): string[] {
  if (!input?.trim()) return [];

  return input
    .split(/[,;\n\r\t]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export interface MergeRecipientsResult {
  tags: Tag[];
  added: number;
  invalid: string[];
  duplicates: number;
  limitReached: boolean;
}

export function mergeRecipientsIntoTags(
  currentTags: Tag[],
  input: string,
  remainingSlots: number,
): MergeRecipientsResult {
  const parsed = parseRecipientInput(input);
  const existing = new Set(currentTags.map((tag) => String(tag.value).toLowerCase()));
  const nextTags = [...currentTags];
  const invalid: string[] = [];
  let added = 0;
  let duplicates = 0;
  let limitReached = false;

  for (const raw of parsed) {
    if (added >= remainingSlots) {
      limitReached = true;
      break;
    }

    if (!isValidEmail(raw)) {
      invalid.push(raw);
      continue;
    }

    const normalized = raw.toLowerCase();
    if (existing.has(normalized)) {
      duplicates += 1;
      continue;
    }

    existing.add(normalized);
    nextTags.push({ label: raw, value: raw });
    added += 1;
  }

  return {
    tags: nextTags,
    added,
    invalid,
    duplicates,
    limitReached,
  };
}

export function getTagsForDisplay(
  tags: Tag[],
  expanded: boolean,
  limit = TAG_DISPLAY_LIMIT,
) {
  if (expanded || tags.length <= limit) {
    return { visible: tags, hiddenCount: 0 };
  }

  return {
    visible: tags.slice(0, limit),
    hiddenCount: tags.length - limit,
  };
}

export function shouldParseBulkPaste(value: string): boolean {
  return /[,;\n\r\t]/.test(value);
}
